<template>
  <div v-if="(isLabelsMode || showLabelsValue) && (labeledSeats || unlabeledSeats || conflictedSeats)">
    <h2 class="text-base">Labels</h2>
    <div v-if="labeledSeats && labeledSeats > 0">
      <b>{{ labeledSeats }}</b>
      labeled seats
    </div>
    <div v-if="unlabeledSeats" class="text-red-600">
      <b>{{ unlabeledSeats }}</b>
      unlabeled seats
    </div>
    <div v-if="conflictedSeats" class="text-red-600">
      <b>{{ conflictedSeats }}</b>
      conflicts
    </div>
    <SMButton
      v-if="isLabelsMode || showLabelsValue"
      :icon="!conflictCheckInProgress ? 'warning' : 'saving-indicator'"
      type="warning"
      label="Check Conflicts"
      class="mt-2"
      :loading="conflictCheckInProgress"
      @click="recheckConflicts"
    />
  </div>
</template>
<script lang="ts">
import { mapState, mapGetters, mapActions } from 'vuex';
import eventbus from 'tiny-emitter/instance';
import { LAYOUT, SEAT } from '@/constants';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import SMIcon from '../ui/SMIcon.vue';
import SMButton from '../ui/SMButton.vue';

export default {
  props: {
    isLabelsMode: {
      type: Boolean,
    },
  },
  data() {
    return {};
  },
  computed: {
    ...mapState('stats', ['unlabeledSeats', 'conflictedSeats']),
    ...mapGetters('stats', ['labeledSeats', 'totalSeats']),
    ...mapGetters('settings', ['isInsideZoneLayout']),
    ...mapState('seatmap', ['showLabels', 'conflictCheckInProgress']),
    showLabelsValue: {
      get() {
        return this.showLabels;
      },
    },
  },
  created() {
    //eventbus.on('seatmap:labels:updated', this.checkLabelsConflicts, this);
    eventbus.on('seatmap:recheck:conflicts', this.checkLabelsConflicts, this);
    //eventbus.on('seatmap:updated', this.checkLabelsConflicts, this);
    eventbus.on('seatmap:labels:updated', this.recalculateUnlabeled, this);
    eventbus.on('seatmap:updated', this.recalculateUnlabeled, this);
    // seatmap mode change
    //eventbus.on('seatmap:mode:changed', this.checkLabelsConflicts, this);
    eventbus.on('seatmap:mode:changed', this.recalculateUnlabeled, this);
  },
  destroyed() {
    //eventbus.off('seatmap:labels:updated', this.checkLabelsConflicts, this);
    eventbus.off('seatmap:recheck:conflicts', this.checkLabelsConflicts, this);
    //eventbus.off('seatmap:updated', this.checkLabelsConflicts, this);
    eventbus.off('seatmap:labels:updated', this.recalculateUnlabeled, this);
    eventbus.off('seatmap:updated', this.recalculateUnlabeled, this);
    // seatmap mode change
    //eventbus.off('seatmap:mode:changed', this.checkLabelsConflicts, this);
    eventbus.off('seatmap:mode:changed', this.recalculateUnlabeled, this);
  },
  methods: {
    ...mapActions('stats', ['changeConflictedSeats', 'changeUnlabeledSeats']),
    ...mapActions('seatmap', ['setConflictCheckStatus']),
    recalculateUnlabeled() {
      const labeledSeats = this.getLabeledUnlabeledSeats(this.$app.currentSection?.uuid);
      const seatsAndTableSeats = this.$app.seatmap.getVisibleSeats(this.$app.currentSection?.uuid);
      this.changeUnlabeledSeats(seatsAndTableSeats.length - labeledSeats.labeled.length);
    },
    seatsHaveSameRowLabel(s1, s2) {
      const s1RowItem = s1.row || s1.table;
      const s2RowItem = s2.row || s2.table;
      return s1RowItem.attributes.label === s2RowItem.attributes.label;
    },
    seatsFromDifferentSections(s1, s2) {
      const s1Section = s1.sid;
      const s2Section = s2.sid;
      return typeof s1Section !== 'undefined' && typeof s2Section !== 'undefined' && s1Section !== s2Section;
    },
    seatsAreConflicted(s1, s2) {
      return (
        s1.attributes.label === s2.attributes.label &&
        this.seatsHaveSameRowLabel(s1, s2) &&
        !this.seatsFromDifferentSections(s1, s2)
      );
    },
    getLabeledUnlabeledSeats(sid = null) {
      const seatsAndTableSeats = this.$app.seatmap.getVisibleSeats(sid);
      return {
        labeled: seatsAndTableSeats.filter((s) => this.seatAndHisRowHaveLabels(s)),
        unlabeled: seatsAndTableSeats.filter((s) => !this.seatAndHisRowHaveLabels(s)),
      };
    },
    seatAndHisRowHaveLabels(seat) {
      let status = seat.attributes.label !== SEAT.LABEL && seat.attributes.label !== '';
      if (seat.table) {
        status = status && seat.table.attributes.label !== SEAT.LABEL && seat.table.attributes.label !== '';
      } else if (seat.row) {
        status = status && seat.row.attributes.label !== SEAT.LABEL && seat.row.attributes.label !== '';
      }
      return status;
    },
    recheckConflicts() {
      this.setConflictCheckStatus(true);
      setTimeout(() => {
        eventbus.emit('seatmap:recheck:conflicts');
      }, 400);
    },
    checkLabelsConflictsPerSections() {
      let conflictedSeats = [];
      let unlabeled = 0;
      for (const section of this.$app.seatmap.sections) {
        const labeledUnlabeledSeats = this.getLabeledUnlabeledSeats(section.uuid);
        const seatsWithLabels = labeledUnlabeledSeats.labeled;
        const seatsWithoutLabels = labeledUnlabeledSeats.unlabeled;
        const conflicts = [];
        for (let sIndex = 0; sIndex < seatsWithLabels.length; sIndex++) {
          const s1 = seatsWithLabels[sIndex];
          let seatLabel = s1.attributes.label || '';
          let rowItemLabel = (s1.row || s1.table).attributes.label || '';
          seatLabel = seatLabel.trim();
          rowItemLabel = rowItemLabel.trim();
          if (s1.attributes.label && rowItemLabel) {
            conflicts[rowItemLabel] = conflicts[rowItemLabel] || [];
            if (!conflicts[rowItemLabel][seatLabel]) {
              const duplicates = seatsWithLabels.slice(sIndex + 1).filter((s2) => this.seatsAreConflicted(s1, s2));
              this.updateConflictedMark(s1, sIndex, duplicates);
              conflicts[rowItemLabel][seatLabel] = !!duplicates.length;
              conflictedSeats.push(...duplicates);
            }
          }
        }
        seatsWithoutLabels.forEach((s) => {
          s.conflicted = false;
        });
        unlabeled += seatsWithoutLabels.length;
      }
      this.$app.unlabeled = unlabeled;
      this.$app.conflicted = conflictedSeats.length;
      // if (conflictedSeats > 0) {
      //   if (!this.$app.conflictedzones) {
      //     this.$app.conflictedzones = [];
      //   }
      //   if (this.$app.currentSection) {
      //     this.$app.conflictedzones.push(this.$app.currentSection.uuid);
      //   }
      //   this.$app.conflictedzones = uniq(this.$app.conflictedzones);
      // } else if (this.$app.conflictedzones && this.$app.conflictedzones.length > 0) {
      //   this.$app.conflictedzones = pull(this.$app.conflictedzones, this.$app.currentSection.uuid);
      // }
      this.$app.conflictedzones = uniq(conflictedSeats.map((s) => s.table?.sid ?? s.sid));
      this.changeConflictedSeats(conflictedSeats.length);
      this.setConflictCheckStatus(false);
      this.$app.handleChangedConflictedZones();
    },
    checkLabelsConflicts() {
      //if (!this.$app.isShowConflicts() && !this.$app.isRecalculateConflicts()) {
      if (!this.$app.isRecalculateConflicts()) {
        return;
      }
      if (this.$app.layout === LAYOUT.SECTIONS) {
        this.checkLabelsConflictsPerSections();
        return;
      }
      const labeledUnlabeledSeats = this.getLabeledUnlabeledSeats(this.$app.currentSection?.uuid);
      const seatsWithLabels = labeledUnlabeledSeats.labeled;
      const seatsWithoutLabels = labeledUnlabeledSeats.unlabeled;
      const conflicts = [];
      let conflictedSeats = [];
      for (let sIndex = 0; sIndex < seatsWithLabels.length; sIndex++) {
        const s1 = seatsWithLabels[sIndex];
        let seatLabel = s1.attributes.label || '';
        let rowItemLabel = (s1.row || s1.table).attributes.label || '';
        seatLabel = seatLabel.trim();
        rowItemLabel = rowItemLabel.trim();
        if (s1.attributes.label && rowItemLabel) {
          conflicts[rowItemLabel] = conflicts[rowItemLabel] || [];
          if (!conflicts[rowItemLabel][seatLabel]) {
            const duplicates = seatsWithLabels.slice(sIndex + 1).filter((s2) => this.seatsAreConflicted(s1, s2));
            this.updateConflictedMark(s1, sIndex, duplicates);
            conflicts[rowItemLabel][seatLabel] = !!duplicates.length;
            conflictedSeats.push(...duplicates);
          }
        }
      }
      seatsWithoutLabels.forEach((s) => {
        s.conflicted = false;
      });
      this.$app.unlabeled = seatsWithoutLabels.length;
      this.$app.conflicted = conflictedSeats.length;
      // if (conflictedSeats > 0) {
      //   if (!this.$app.conflictedzones) {
      //     this.$app.conflictedzones = [];
      //   }
      //   if (this.$app.currentSection) {
      //     this.$app.conflictedzones.push(this.$app.currentSection.uuid);
      //   }
      //   this.$app.conflictedzones = uniq(this.$app.conflictedzones);
      // } else if (this.$app.conflictedzones && this.$app.conflictedzones.length > 0) {
      //   this.$app.conflictedzones = pull(this.$app.conflictedzones, this.$app.currentSection.uuid);
      // }
      this.$app.conflictedzones = uniq(conflictedSeats.map((s) => s.table?.sid ?? s.sid));
      this.changeConflictedSeats(conflictedSeats.length);
      this.setConflictCheckStatus(false);
      this.$app.handleChangedConflictedZones();
    },
    updateConflictedMark(s1, sIndex, duplicates) {
      if (!duplicates.length) {
        s1.conflicted = false;
      } else {
        duplicates.forEach((d) => {
          d.conflicted = true;
        });
        s1.conflicted = true;
      }
    },
  },
  components: { SMIcon, SMButton },
};
</script>
```
